/**
 * Enhanced Shopify Product Card - Domain Integration
 * 
 * Upgraded ShopifyProductCard that uses domain types internally while maintaining
 * backward compatibility with legacy Shopify product interfaces.
 */

import { ShopifyProduct, ShopifyVariant, useShopifyCart, useRecentlyViewed } from '@/hooks';
import { useFavorites } from "@/contexts/FavoritesContext";
import { ShopifyProductCard } from '@/components/ShopifyProductCard';
import { productionLogger } from '@/utils/production-logger';
import { useStandardQuickView } from '@/hooks/useStandardQuickView';

interface EnhancedShopifyProductCardProps {
  product: ShopifyProduct;
  size?: 'large' | 'medium' | 'small';
  redirectOnAdd?: boolean;
  matchedVariant?: ShopifyVariant | null;
  columnCount?: 2 | 3 | 4;
  viewSize?: 'small' | 'large' | 'gallery';
  showPurchaseControls?: boolean;
  hideCustomBadges?: boolean;
  onImageLoad?: () => void;
}

export const EnhancedShopifyProductCard = ({
  product,
  size = 'large',
  redirectOnAdd = false,
  matchedVariant,
  columnCount,
  viewSize = 'large',
  showPurchaseControls = false,
  hideCustomBadges = false,
  onImageLoad
}: EnhancedShopifyProductCardProps) => {
  const { handleQuickView } = useStandardQuickView();
  
  // Legacy hooks for backward compatibility
  const { addToCart: legacyAddToCart } = useShopifyCart();
  
  // Safe favorites usage with error handling to prevent context crashes
  let favoritesContext: any = null;
  try {
    favoritesContext = useFavorites();
  } catch (error) {
    productionLogger.warn('FavoritesContext not available yet:', error);
  }
  
  const { addToRecentlyViewed } = useRecentlyViewed();

  // Use ShopifyProductCard directly - it already handles sale display properly via UnifiedProductCard
  return (
    <>
      <ShopifyProductCard
        product={product}
        size={size}
        redirectOnAdd={redirectOnAdd}
        matchedVariant={matchedVariant}
        columnCount={columnCount}
        viewSize={viewSize}
        showPurchaseControls={showPurchaseControls}
        hideCustomBadges={hideCustomBadges}
        onImageLoad={onImageLoad}
      />
    </>
  );
};